
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile, UserRole } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface CreateUserData {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: UserRole;
}

export const useUserManagement = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const createUser = async (userData: CreateUserData) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-user-invitation', {
        body: userData,
      });

      if (error) throw error;

      toast({
        title: 'Usuario creado',
        description: 'Se ha enviado la invitación por correo electrónico.',
      });

      return { data, error: null };
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al crear el usuario',
        variant: 'destructive',
      });
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const resendInvitation = async (userId: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('resend-invitation', {
        body: { userId },
      });

      if (error) throw error;

      toast({
        title: 'Invitación reenviada',
        description: 'Se ha reenviado la invitación por correo electrónico.',
      });

      return { error: null };
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al reenviar la invitación',
        variant: 'destructive',
      });
      return { error };
    } finally {
      setLoading(false);
    }
  };

  const assignProperties = async (userId: string, propertyIds: string[]) => {
    setLoading(true);
    try {
      // Primero eliminar asignaciones existentes
      await supabase
        .from('user_property_assignments')
        .delete()
        .eq('user_id', userId);

      // Luego insertar las nuevas asignaciones
      if (propertyIds.length > 0) {
        const assignments = propertyIds.map(propertyId => ({
          user_id: userId,
          property_id: propertyId,
        }));

        const { error } = await supabase
          .from('user_property_assignments')
          .insert(assignments);

        if (error) throw error;
      }

      toast({
        title: 'Propiedades asignadas',
        description: 'Se han actualizado las propiedades del usuario.',
      });

      return { error: null };
    } catch (error: any) {
      console.error('Error assigning properties:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al asignar propiedades',
        variant: 'destructive',
      });
      return { error };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    createUser,
    resendInvitation,
    assignProperties,
  };
};
